import { ArrowRight, Mail, Phone, Lock, Send, CheckCircle } from 'lucide-react';
import { useState } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function FranchisePage() {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    location: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/franchise-interest`, formData);
      setSubmitSuccess(true);
    } catch (error) {
      console.error('Franchise interest error:', error);
      // Still show success for UX (the data was likely saved)
      setSubmitSuccess(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const updateFormData = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="min-h-screen pt-24" data-testid="franchise-page">
      {/* Hero Section */}
      <section className="py-24 md:py-32 bg-black text-white">
        <div className="container mx-auto px-4 md:px-8 max-w-4xl text-center">
          <div
            
            
            
          >
            <p className="text-xs tracking-[0.3em] text-neutral-400 uppercase mb-6">Franchise Opportunity</p>
            <h1 className="font-serif text-5xl md:text-7xl font-bold mb-6">
              Grow with <span className="italic">Us</span>
            </h1>
            <p className="text-neutral-400 text-lg max-w-2xl mx-auto mb-8">
              Join the Honey I'm Home Coffee family. We're looking for passionate entrepreneurs 
              who want to bring exceptional coffee and our family recipes to their communities.
            </p>
          </div>
        </div>
      </section>

      {/* What We Offer - Brief Overview */}
      <section className="py-24 md:py-32 bg-white" data-testid="franchise-overview">
        <div className="container mx-auto px-4 md:px-8 max-w-6xl">
          <div className="text-center mb-16">
            <p className="text-xs tracking-[0.2em] text-neutral-500 uppercase mb-4">The Opportunity</p>
            <h2 className="font-serif text-4xl md:text-5xl font-semibold text-black">
              Why <span className="italic">Partner</span> With Us
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div
              
              
              
              className="text-center p-8 bg-[#FAFAF5]"
            >
              <div className="text-5xl mb-6">☕</div>
              <h3 className="font-serif text-xl font-semibold text-black mb-4">Proven Concept</h3>
              <p className="text-neutral-600 text-sm leading-relaxed">
                Join a brand with a track record of success, 5-star reviews, and a loyal customer base.
              </p>
            </div>

            <div
              
              
              
              
              className="text-center p-8 bg-[#FAFAF5]"
            >
              <div className="text-5xl mb-6">🍜</div>
              <h3 className="font-serif text-xl font-semibold text-black mb-4">Unique Offering</h3>
              <p className="text-neutral-600 text-sm leading-relaxed">
                Access to our secret family recipes and our signature organic houseblend coffee.
              </p>
            </div>

            <div
              
              
              
              
              className="text-center p-8 bg-[#FAFAF5]"
            >
              <div className="text-5xl mb-6">📈</div>
              <h3 className="font-serif text-xl font-semibold text-black mb-4">Full Support</h3>
              <p className="text-neutral-600 text-sm leading-relaxed">
                Comprehensive training, site selection support, and ongoing business guidance.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Gated Access Notice */}
      <section className="py-16 bg-[#FAFAF5] border-y border-neutral-200">
        <div className="container mx-auto px-4 md:px-8 max-w-4xl text-center">
          <div
            
            
            
            className="flex items-center justify-center gap-3 text-neutral-600"
          >
            <Lock size={20} />
            <p className="text-sm">
              Detailed investment information and franchise requirements are shared with qualified applicants 
              after an initial review of your expression of interest.
            </p>
          </div>
        </div>
      </section>

      {/* Expression of Interest Form */}
      <section id="enquiry-form" className="py-24 md:py-32 bg-white" data-testid="interest-form-section">
        <div className="container mx-auto px-4 md:px-8 max-w-2xl">
          <div className="text-center mb-12">
            <p className="text-xs tracking-[0.2em] text-neutral-500 uppercase mb-4">Get Started</p>
            <h2 className="font-serif text-4xl md:text-5xl font-semibold text-black mb-4">
              Express Your <span className="italic">Interest</span>
            </h2>
            <p className="text-neutral-600 max-w-xl mx-auto">
              Submit your details below and our franchise team will be in touch to discuss the opportunity 
              and share detailed information about investment requirements.
            </p>
          </div>

          {submitSuccess ? (
            <div
              
              
              className="text-center py-16 bg-[#FAFAF5]"
              data-testid="franchise-success"
            >
              <CheckCircle size={64} className="mx-auto mb-6 text-black" />
              <h2 className="font-serif text-3xl font-semibold mb-4">Thank You!</h2>
              <p className="text-neutral-600 mb-4 max-w-md mx-auto">
                We've received your expression of interest. Our franchise team will review your application 
                and contact you within 48 hours with more information.
              </p>
              <p className="text-neutral-500 text-sm">
                In the meantime, feel free to visit our café to experience what Honey I'm Home is all about.
              </p>
            </div>
          ) : (
            <form 
              onSubmit={handleSubmit} 
              className="space-y-6 bg-[#FAFAF5] p-8 md:p-12"
              
              
              data-testid="franchise-interest-form"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-xs tracking-wider uppercase text-neutral-500 mb-2">
                    First Name *
                  </label>
                  <input
                    type="text"
                    value={formData.firstName}
                    onChange={(e) => updateFormData('firstName', e.target.value)}
                    data-testid="franchise-firstname"
                    className="w-full bg-white border border-neutral-200 px-4 py-3 text-black focus:border-black focus:outline-none"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs tracking-wider uppercase text-neutral-500 mb-2">
                    Last Name *
                  </label>
                  <input
                    type="text"
                    value={formData.lastName}
                    onChange={(e) => updateFormData('lastName', e.target.value)}
                    data-testid="franchise-lastname"
                    className="w-full bg-white border border-neutral-200 px-4 py-3 text-black focus:border-black focus:outline-none"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs tracking-wider uppercase text-neutral-500 mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => updateFormData('email', e.target.value)}
                  data-testid="franchise-email"
                  className="w-full bg-white border border-neutral-200 px-4 py-3 text-black focus:border-black focus:outline-none"
                  required
                />
              </div>

              <div>
                <label className="block text-xs tracking-wider uppercase text-neutral-500 mb-2">
                  Phone Number *
                </label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => updateFormData('phone', e.target.value)}
                  data-testid="franchise-phone"
                  className="w-full bg-white border border-neutral-200 px-4 py-3 text-black focus:border-black focus:outline-none"
                  required
                />
              </div>

              <div>
                <label className="block text-xs tracking-wider uppercase text-neutral-500 mb-2">
                  Preferred Location/Area *
                </label>
                <input
                  type="text"
                  value={formData.location}
                  onChange={(e) => updateFormData('location', e.target.value)}
                  placeholder="City or region you're interested in"
                  data-testid="franchise-location"
                  className="w-full bg-white border border-neutral-200 px-4 py-3 text-black focus:border-black focus:outline-none placeholder:text-neutral-400"
                  required
                />
              </div>

              <div>
                <label className="block text-xs tracking-wider uppercase text-neutral-500 mb-2">
                  Tell Us About Yourself
                </label>
                <textarea
                  value={formData.message}
                  onChange={(e) => updateFormData('message', e.target.value)}
                  placeholder="Share your background and why you're interested in a Honey I'm Home franchise"
                  data-testid="franchise-message"
                  rows={4}
                  className="w-full bg-white border border-neutral-200 px-4 py-3 text-black focus:border-black focus:outline-none resize-none placeholder:text-neutral-400"
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                data-testid="franchise-submit"
                className="w-full bg-black text-white px-8 py-4 text-xs tracking-widest uppercase font-bold hover:bg-neutral-800 transition-all disabled:opacity-50 flex items-center justify-center"
              >
                {isSubmitting ? (
                  'Submitting...'
                ) : (
                  <>
                    Submit Interest
                    <Send size={16} className="ml-2" />
                  </>
                )}
              </button>

              <p className="text-center text-neutral-500 text-xs">
                By submitting this form, you agree to our privacy policy. 
                Your information will only be used for franchise enquiry purposes.
              </p>
            </form>
          )}
        </div>
      </section>

      {/* Direct Contact Section */}
      <section className="py-24 bg-black text-white">
        <div className="container mx-auto px-4 md:px-8 max-w-4xl">
          <div className="text-center mb-12">
            <h2 className="font-serif text-3xl md:text-4xl font-semibold mb-4">
              Prefer to Talk <span className="italic">Directly?</span>
            </h2>
            <p className="text-neutral-400">
              Our franchise team is happy to answer any initial questions you may have.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-2xl mx-auto">
            <a
              href="mailto:Enquiries@honeyimhomecoffee.com"
              
              
              
              className="flex items-center justify-center gap-4 bg-white/10 p-6 hover:bg-white/20 transition-colors"
              data-testid="franchise-email-cta"
            >
              <Mail size={24} />
              <div className="text-left">
                <p className="text-xs tracking-wider uppercase text-neutral-400 mb-1">Email Us</p>
                <p className="text-sm">Enquiries@honeyimhomecoffee.com</p>
              </div>
            </a>
            
            <a
              href="tel:+447739083131"
              
              
              
              
              className="flex items-center justify-center gap-4 bg-white/10 p-6 hover:bg-white/20 transition-colors"
              data-testid="franchise-phone-cta"
            >
              <Phone size={24} />
              <div className="text-left">
                <p className="text-xs tracking-wider uppercase text-neutral-400 mb-1">Call Us</p>
                <p className="text-sm">+44 7739 083131</p>
              </div>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
